import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Moon, Sun, Package, User, HelpCircle, Menu, X } from 'lucide-react';
import AppLogo from '../AppLogo';

// Ancres de la page, dans l'ordre de lecture.
const SECTIONS = (t) => [
  { id: 'benefits', label: t('navBenefits') },
  { id: 'solutions', label: t('navSolutions') },
  { id: 'demo', label: t('navDemo') },
  { id: 'trust', label: t('navTrust') },
];

const Navbar = ({ scrollTo, anchors = true }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  // L'en-tête se pose sur la photo du hero et ne devient opaque qu'au défilement.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = useCallback((action) => {
    setMobileOpen(false);
    action();
  }, []);

  return (
    <>
      {/* Hors de l'en-tête : celui-ci porte un backdrop-filter au défilement, qui
          ferait de lui le bloc conteneur d'un enfant en position fixe. */}
      <button
        type="button"
        onClick={toggleTheme}
        className="landing-theme-toggle"
        aria-label={language === 'en' ? 'Switch theme' : 'Changer de thème'}
      >
        {theme === 'light' ? <Moon /> : <Sun />}
      </button>

      <header className={`landing-header${scrolled || mobileOpen ? ' landing-header--solid' : ''}`}>

      {/* Top bar — logo LEFT, links + actions RIGHT */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-nav-logo">
            <div className="landing-nav-logo-icon">
              <AppLogo size={24} />
            </div>
            FretManager
          </div>

          <div className="landing-nav-right">
            <div className="landing-nav-links">
              <button onClick={() => navigate('/track')} className="landing-nav-link">
                <Package size={14} style={{ marginRight: 4 }} />
                {t('trackAPackage')}
              </button>
            </div>

            <div className="landing-nav-sep" />

            <div className="landing-nav-actions">
              <button onClick={() => navigate('/login')} className="landing-nav-link">
                <User size={14} style={{ marginRight: 4 }} />
                {t('login')}
              </button>
              <button onClick={() => navigate('/contact')} className="btn-primary">
                {t('navRequestDemo')}
              </button>
            </div>
          </div>

          <button
            className="landing-nav-burger"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="landing-mobile-menu">
          {anchors && SECTIONS(t).map(({ id, label }) => (
            <button key={id} onClick={() => handleNav(() => scrollTo(id))} className="landing-mobile-link">
              {label}
            </button>
          ))}
          <button onClick={() => handleNav(() => navigate('/track'))} className="landing-mobile-link">
            <Package size={16} />
            {t('trackAPackage')}
          </button>
          <div className="landing-mobile-sep" />
          <button onClick={() => handleNav(() => navigate('/login'))} className="landing-mobile-link">
            <User size={16} />
            {t('login')}
          </button>
          <button onClick={() => handleNav(() => navigate('/register-company'))} className="btn-primary landing-mobile-cta">
            {t('createCompany')}
          </button>
        </div>
      )}

      {/* Sous-barre : intitulé de la page à gauche, ancres à droite */}
      <div className="landing-nav-sub">
        <div className="landing-nav-sub-inner">
          <span className="landing-nav-sub-title">{t('navSectionLabel')}</span>
          <div className="landing-nav-sub-links">
            {anchors && SECTIONS(t).map(({ id, label }) => (
              <button key={id} className="landing-nav-sub-link" onClick={() => scrollTo(id)}>
                {label}
              </button>
            ))}
            <button className="landing-nav-sub-link" onClick={() => navigate('/help')}>
              <HelpCircle size={14} />
              {t('help')}
            </button>
          </div>
        </div>
      </div>
      </header>
    </>
  );
};

export default Navbar;
